/**
 * The rules behind the admin course page (`/courses/:id`). Pure, so they are
 * tested without a component tree; every card reads its numbers and its words
 * from here.
 *
 * A batch is read with the batch page's own rules (`batchDetails.js`), so its
 * timeline, seats and instructors cannot say one thing here and another there.
 */
import { isHtml, stripHtml } from "../../../utils/richText";
import { parseFeeText } from "../../../utils/enrolmentFee";
import { formatCurrency } from "../../../utils/format";
import { batchInstructors, seatUsage } from "../../Batches/BatchDetails/batchDetails";

export { batchInstructors, seatUsage };

export const plural = (n, word, many = `${word}s`) => `${n} ${n === 1 ? word : many}`;

const toTime = (value) => {
  if (!value) return null;
  const t = new Date(value).getTime();
  return Number.isNaN(t) ? null : t;
};

const unique = (items) => {
  const seen = new Set();
  return items.filter((item) => {
    const key = item.toLowerCase();
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
};

/* ── The course itself ───────────────────────────────────────────────────── */

/**
 * The course's highlights as a list of sentences. The field is the editor's
 * HTML (a bulleted list, or one paragraph per point) or, on older courses,
 * plain text separated by lines or commas.
 *
 * ⚠️ An empty field is an EMPTY list — never stand-in highlights. The admin
 * must see exactly what their students are shown, and that includes nothing.
 */
export function courseHighlights(raw) {
  if (typeof raw !== "string" || !raw.trim()) return [];
  if (isHtml(raw)) {
    const items = [...raw.matchAll(/<li\b[^>]*>([\s\S]*?)<\/li>/gi)].map((m) => stripHtml(m[1]));
    const points = items.length ? items : raw.split(/<\/(?:p|div|h[1-6])>|<br\s*\/?>/i).map(stripHtml);
    return unique(points.filter(Boolean));
  }
  const parts = raw.includes("\n") ? raw.split("\n") : raw.split(",");
  return unique(parts.map((s) => s.replace(/^[\s•*-]+/, "").trim()).filter(Boolean));
}

/** The skill chips (`cms.tags`) — trimmed, de-duplicated, never blank. */
export function courseSkills(course) {
  const tags = Array.isArray(course?.cms?.tags) ? course.cms.tags : [];
  return unique(tags.map((t) => String(t ?? "").trim()).filter(Boolean));
}

/**
 * The course fee as typed (`Course.fees` is free text). A plain figure —
 * "50000", "₹50,000", "Rs. 50,000/-" — is shown in the house format; anything
 * more ("50,000 + GST") is shown as written, because reformatting it would
 * drop the words. `amount` is the first figure either way, the same reading
 * the student form and self-enrolment make (`parseFeeText`).
 */
export function courseFee(course) {
  const raw = course?.fees;
  const text = raw === undefined || raw === null ? "" : String(raw).trim();
  const amount = parseFeeText(text);
  if (!text || amount === null) return { amount: null, text: text || null };
  const bare = text.replace(/rs\.?|inr|₹|,|\/-|\s/gi, "");
  return { amount, text: /^\d+(\.\d+)?$/.test(bare) ? formatCurrency(amount) : text };
}

/**
 * Where the course is offered: every branch, or the named ones. `branches` is
 * BranchContext's list, which holds only the branches the caller may open —
 * a branch outside it is counted, never named.
 */
export function offeredAt(course, branches = []) {
  if (course?.branchScope !== "branches") return { everywhere: true, names: [], hidden: 0 };
  const byId = new Map(branches.map((b) => [String(b._id), b.name]));
  const ids = (course.branchIds || []).map((b) => String(b?._id ?? b));
  const names = ids.map((id) => byId.get(id)).filter(Boolean);
  return { everywhere: false, names, hidden: ids.length - names.length };
}

/* ── Its batches ─────────────────────────────────────────────────────────── */

const PHASE_OF_STATUS = { ongoing: "running", upcoming: "upcoming", completed: "completed" };

/**
 * "running" | "upcoming" | "completed" — read from the batch's STATUS, which
 * Start batch and End batch set. A batch whose start date has passed but which
 * nobody started is still upcoming: its classes have no plan yet.
 */
export const batchPhase = (batch) =>
  PHASE_OF_STATUS[String(batch?.status || "upcoming").toLowerCase()] || "upcoming";

export function phaseCounts(batches = []) {
  const counts = { all: batches.length, running: 0, upcoming: 0, completed: 0 };
  for (const b of batches) counts[batchPhase(b)] += 1;
  return counts;
}

const PHASE_RANK = { running: 0, upcoming: 1, completed: 2 };

/**
 * The order an admin reads the list in: what is running, then what starts
 * next (soonest first), then what has finished (most recent first). An
 * undated batch goes to the end of its group.
 */
export function orderBatches(batches = []) {
  const keyOf = (b) => {
    const phase = batchPhase(b);
    if (phase === "upcoming") return toTime(b.startDate);
    const t = toTime(phase === "completed" ? b.endDate || b.startDate : b.startDate);
    return t === null ? null : -t;
  };
  return [...batches].sort((a, b) => {
    const rank = PHASE_RANK[batchPhase(a)] - PHASE_RANK[batchPhase(b)];
    if (rank) return rank;
    const x = keyOf(a);
    const y = keyOf(b);
    if (x === null || y === null) return x === null && y === null ? 0 : x === null ? 1 : -1;
    return x - y;
  });
}

/**
 * A date's calendar day in India, as a day number. A start date saved as UTC
 * midnight and one saved as IST midnight both land on the day that was typed.
 */
const DAY_MS = 86400000;
const IST_OFFSET_MS = 5.5 * 3600000;
const istDay = (t) => Math.floor((t + IST_OFFSET_MS) / DAY_MS);

/**
 * The next batch to begin: an upcoming one starting today or later, soonest
 * first. `days` is whole calendar days away (0 = today). null when none is
 * scheduled.
 */
export function nextBatch(batches = [], now = Date.now()) {
  const today = istDay(now);
  let next = null;
  for (const b of batches) {
    const start = toTime(b.startDate);
    if (batchPhase(b) !== "upcoming" || start === null) continue;
    const days = istDay(start) - today;
    if (days < 0) continue;
    if (!next || start < next.start) next = { batch: b, start, days };
  }
  return next && { batch: next.batch, days: next.days };
}

/**
 * Upcoming batches whose start date has passed without anyone pressing Start
 * batch — so they have no class plan and their students see no schedule.
 */
export const overdueStarts = (batches = [], now = Date.now()) =>
  orderBatches(batches).filter((b) => {
    const start = toTime(b.startDate);
    return batchPhase(b) === "upcoming" && start !== null && istDay(start) < istDay(now);
  });

/** "today" · "tomorrow" · "in 12 days". */
export const inDays = (days) => (days <= 0 ? "today" : days === 1 ? "tomorrow" : `in ${days} days`);

/* ── Its syllabus ────────────────────────────────────────────────────────── */

/**
 * The active syllabus in one line, or null when there is none.
 *   { version, type: "day" | "module", count, unit, topics, label }
 */
export function syllabusSummary(template) {
  if (!template) return null;
  const isModule = template.syllabusType === "module";
  const parts = isModule ? template.modules || [] : template.days || [];
  if (!parts.length) return null;
  const topics = isModule
    ? parts.reduce((n, m) => n + (m.topics || []).length, 0)
    : parts.reduce((n, d) => n + (d.topics || []).filter((t) => String(t || "").trim()).length, 0);
  const unit = isModule ? (parts.length === 1 ? "module" : "modules") : (parts.length === 1 ? "day" : "days");
  return {
    version: template.version || 1,
    type: isModule ? "module" : "day",
    count: parts.length,
    unit,
    topics,
    label: `${isModule ? "Module-wise" : "Day-wise"} · ${parts.length} ${unit}`,
  };
}

/* ── Its files ───────────────────────────────────────────────────────────── */

const VIDEO_TYPES = new Set(["MP4", "MOV", "WEBM", "MKV"]);

export const isVideoFile = (m) => !!m?.isVideo || VIDEO_TYPES.has(String(m?.fileType || "").toUpperCase());

/** Course-level files split the way the tabs show them. */
export function splitFiles(materials = []) {
  const videos = [];
  const documents = [];
  for (const m of materials) (isVideoFile(m) ? videos : documents).push(m);
  return { videos, documents };
}

export const MATERIAL_CATEGORIES = [
  { value: "ppt", label: "Presentation" },
  { value: "notes", label: "Notes" },
  { value: "script", label: "Script" },
  { value: "other", label: "Other" },
];

export const categoryLabel = (value) =>
  MATERIAL_CATEGORIES.find((c) => c.value === value)?.label || "Other";

/** "1h 20m" · "45m" — a video's length from `duration` in minutes. */
export function videoLength(minutes) {
  const m = Math.round(Number(minutes) || 0);
  if (m <= 0) return null;
  if (m < 60) return `${m}m`;
  return m % 60 ? `${Math.floor(m / 60)}h ${m % 60}m` : `${m / 60}h`;
}

/* ── What needs attention ────────────────────────────────────────────────── */

/**
 * The prompts above the vitals — only the ones that are true, and only once
 * the page knows (a figure still loading never raises one).
 *
 *   no-syllabus  — batches started from this course would get no class plan
 *   not-started  — upcoming batches past their start date (`batches` lists them)
 *   no-next      — every batch has finished and none is scheduled
 *   unplaced     — students hold the course but sit in none of its batches
 *
 * An empty course raises no batch prompt: the Batches tab's empty state is
 * already the call to add one, and saying it twice is noise.
 */
export function attentionItems({ batches, syllabus, headcount, now = Date.now() }) {
  const items = [];
  if (syllabus.known && !syllabus.summary) items.push({ key: "no-syllabus" });
  if (batches.known) {
    const late = overdueStarts(batches.list, now);
    if (late.length) items.push({ key: "not-started", batches: late });
    const counts = phaseCounts(batches.list);
    if (counts.all > 0 && counts.running === 0 && counts.upcoming === 0) items.push({ key: "no-next" });
  }
  if (headcount?.withoutBatch > 0) items.push({ key: "unplaced", count: headcount.withoutBatch });
  return items;
}
